import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiError } from '../api';
import PageHeader from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { CalendarClock, ExternalLink, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import SortHeader from '../components/SortHeader';
import BulkDeleteDialog from '../components/BulkDeleteDialog';
import { useTableTools, useSortedItems } from '../components/tableTools';

const GBP = (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0 }).format(Number(v || 0));

const SORT_ACCESSORS = {
    contract_number: c => c.contract_number || '',
    client_name:     c => (c.client_name || '').toLowerCase(),
    mrr:             c => Number(c.monthly_saas_fee || 0),
    arr:             c => Number(c.monthly_saas_fee || 0) * 12,
    renewal_date:    c => c.renewal_date || '',
    days:            c => {
        try { return Math.ceil((new Date(c.renewal_date) - new Date()) / 86400000); } catch { return 0; }
    },
};

export default function Renewals() {
    const [buckets, setBuckets] = useState({ d7: [], d30: [], d60: [], d90: [] });
    const [loading, setLoading] = useState(true);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';
    const { selected, toggleOne, clear, sortKey, sortDir, changeSort, setSelected } = useTableTools({ defaultSortKey: 'renewal_date' });

    const toggleBucket = useCallback((bucketItems) => {
        setSelected(prev => {
            const allSelected = bucketItems.length > 0 && bucketItems.every(c => prev.has(c.id));
            const next = new Set(prev);
            if (allSelected) {
                bucketItems.forEach(c => next.delete(c.id));
            } else {
                bucketItems.forEach(c => next.add(c.id));
            }
            return next;
        });
    }, [setSelected]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const windows = [7, 30, 60, 90];
            const results = await Promise.all(windows.map(w => api.get('/contracts', { params: { renewal_window_days: w, status: 'active' } })));
            setBuckets({
                d7:  results[0].data.items,
                d30: results[1].data.items.filter(c => !results[0].data.items.find(x => x.id === c.id)),
                d60: results[2].data.items.filter(c => !results[1].data.items.find(x => x.id === c.id)),
                d90: results[3].data.items.filter(c => !results[2].data.items.find(x => x.id === c.id)),
            });
            clear();
        } catch (e) { toast.error(e.message); }
        finally { setLoading(false); }
    }, [clear]);

    useEffect(() => { load(); }, [load]);

    const totalCount = buckets.d7.length + buckets.d30.length + buckets.d60.length + buckets.d90.length;

    const openDeleteDialog = async () => {
        const ids = Array.from(selected);
        if (ids.length === 0) return;
        try {
            const r = await api.post('/contracts/bulk-delete-preview', { ids });
            setConfirmDelete({ counts: r.data.counts, names: r.data.sample_names, ids });
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Failed'); }
    };

    const doDelete = async (ids) => {
        try {
            const r = await api.post('/contracts/bulk-delete', { ids });
            const d = r.data.deleted || {};
            toast.success(`Deleted ${d.contracts || 0} contract(s) + ${(d.invoices || 0) + (d.payments || 0)} linked records`);
            load();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Delete failed'); }
    };

    const bucketProps = { isAdmin, selected, toggleOne, toggleBucket, sortKey, sortDir, changeSort, onOpen: navigate };

    return (
        <div data-testid="renewals-page" className="bm-fade-in">
            <PageHeader
                title="Renewals"
                description="Contracts coming up for renewal — stay ahead and protect MRR."
                actions={isAdmin && selected.size > 0 ? (
                    <Button onClick={openDeleteDialog} variant="outline" className="h-9 text-red-600 border-red-200 hover:bg-red-50" data-testid="renewals-bulk-delete-btn">
                        <Trash2 size={15} className="mr-1.5" /> Delete {selected.size} selected
                    </Button>
                ) : undefined}
            />
            <div className="px-8 py-6">
                {loading && <div className="text-[color:var(--bm-text-muted)]">Loading…</div>}
                {!loading && totalCount === 0 && (
                    <div className="bg-white rounded-xl border border-[color:var(--bm-border)] px-6 py-14 text-center" data-testid="renewals-empty">
                        <CalendarClock size={36} className="mx-auto text-[color:var(--bm-text-subtle)]" />
                        <div className="mt-3 text-[color:var(--bm-text-muted)] text-[14px]">No renewals in the next 90 days.</div>
                    </div>
                )}
                {!loading && totalCount > 0 && (
                    <div className="space-y-8">
                        <Bucket title="Next 7 days"  tone="red"     items={buckets.d7}  testId="renewals-d7"  {...bucketProps} />
                        <Bucket title="Next 30 days" tone="amber"   items={buckets.d30} testId="renewals-d30" {...bucketProps} />
                        <Bucket title="Next 60 days" tone="sky"     items={buckets.d60} testId="renewals-d60" {...bucketProps} />
                        <Bucket title="Next 90 days" tone="emerald" items={buckets.d90} testId="renewals-d90" {...bucketProps} />
                    </div>
                )}
            </div>
            <BulkDeleteDialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} info={confirmDelete}
                              entityLabel="contract" onConfirm={doDelete} testId="renewals-bulk-delete-dialog" />
        </div>
    );
}

function Bucket({ title, tone, items, testId, onOpen, isAdmin, selected, toggleOne, toggleBucket, sortKey, sortDir, changeSort }) {
    const sorted = useSortedItems(items, sortKey, sortDir, SORT_ACCESSORS);
    if (items.length === 0) return null;
    const allBucketSelected = items.length > 0 && items.every(c => selected.has(c.id));
    const colSpan = isAdmin ? 8 : 7;
    const tones = {
        red:     'border-red-200 bg-red-50 text-red-800',
        amber:   'border-amber-200 bg-amber-50 text-amber-800',
        sky:     'border-sky-200 bg-sky-50 text-sky-800',
        emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    };
    return (
        <div data-testid={testId}>
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11.5px] font-semibold border mb-3 ${tones[tone]}`}>
                <CalendarClock size={13} /> {title} · {items.length}
            </div>
            <div className="bg-white rounded-xl border border-[color:var(--bm-border)] overflow-hidden">
                <table className="w-full text-[13px]">
                    <thead className="bg-[color:var(--bm-bg-mute)] text-[color:var(--bm-text-muted)]">
                        <tr>
                            {isAdmin && (
                                <th className="w-10 px-4 py-3">
                                    <input type="checkbox" checked={allBucketSelected}
                                           onChange={() => toggleBucket(items)} className="cursor-pointer"
                                           data-testid={`${testId}-select-all`} />
                                </th>
                            )}
                            <SortHeader label="Contract"     sortKey="contract_number" currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} />
                            <SortHeader label="Client"       sortKey="client_name"     currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} />
                            <SortHeader label="MRR"          sortKey="mrr"             currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} align="right" />
                            <SortHeader label="ARR"          sortKey="arr"             currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} align="right" />
                            <SortHeader label="Renewal date" sortKey="renewal_date"    currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} />
                            <SortHeader label="Days"         sortKey="days"            currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} align="right" />
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map(c => {
                            const days = Math.ceil((new Date(c.renewal_date) - new Date()) / 86400000);
                            const mrr = c.monthly_saas_fee || 0;
                            const isSel = selected.has(c.id);
                            return (
                                <tr key={c.id} onClick={() => onOpen(`/contracts/${c.id}`)}
                                    className={`border-t border-[color:var(--bm-border)] hover:bg-[color:var(--bm-bg-mute)] cursor-pointer ${isSel ? 'bg-[color:var(--bm-accent-50)]/60' : ''}`}
                                    data-testid={`renewal-row-${c.id}`}>
                                    {isAdmin && (
                                        <td className="px-4 py-3.5" onClick={(e) => { e.stopPropagation(); toggleOne(c.id); }}>
                                            <input type="checkbox" checked={isSel} onChange={() => toggleOne(c.id)}
                                                   onClick={(e) => e.stopPropagation()} className="cursor-pointer"
                                                   data-testid={`renewal-select-${c.id}`} />
                                        </td>
                                    )}
                                    <td className="px-5 py-3.5 font-medium">{c.contract_number}</td>
                                    <td className="px-4 py-3.5 text-[color:var(--bm-text-muted)]">{c.client_name}</td>
                                    <td className="px-4 py-3.5 text-right">{GBP(mrr)}</td>
                                    <td className="px-4 py-3.5 text-right font-medium">{GBP(mrr * 12)}</td>
                                    <td className="px-4 py-3.5 text-[color:var(--bm-text-muted)]">{c.renewal_date}</td>
                                    <td className="px-4 py-3.5 text-right text-[color:var(--bm-text-muted)]">{days}d</td>
                                    <td className="px-4 py-3.5 text-right"><ExternalLink size={14} className="text-[color:var(--bm-text-subtle)]" /></td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
